import type { BattleUnit, Hex } from '@/core/types/battle.types';

import { 
    type Goal, 
    GoalType, 
    type GoalAssignment,
    type GoalSuitabilityEvaluator,
    type ManagerState
} from '@/core/battle/ai/strategic/AIGoals';
import { coordsToId, getHexDistance, hexRound } from '@/core/utils/hex.utils';
import { PlanningContext } from '@/core/battle/ai/types/ai.types';
import { CombatCalculator } from '@/core/battle/CombatCalculator';
import { EvaluatorUtils } from '@/core/battle/ai/utils/evaluatorUtils';
import { getUnitTotalHp } from '@/core/utils/battle.utils';

export class AIStrategicManager {
    private state: ManagerState = {
        goals: [],
        assignments: new Map(),
        goalProgress: new Map(),
        goalsCreated: 0,
        goalsCompleted: 0,
        goalsFailed: 0,
    };
    
    private currentTurn: number = 0;
    private currentUnit: string = '-';
    
    // ═══════════════════════════════════════════════════════════════════════
    // PUBLIC API
    // ═══════════════════════════════════════════════════════════════════════
    
    onTurnStart(
        allies: BattleUnit[],
        enemies: BattleUnit[],
        battleMap: Map<string, Hex>,
        turn: number,
    ): void {
        this.currentTurn = turn;
        this.state.goals = [];
        this.state.assignments.clear();

        const enemyCentroid = this.getCentroid(enemies);
        const allyCentroid = this.getCentroid(allies);
        const centroidDist = getHexDistance(enemyCentroid, allyCentroid);

        const slowest = allies.reduce((min, a) => 
            (a.moveCostMin ?? 40) > (min.moveCostMin ?? 40) ? a : min
        );
        const hexesPerRound = Math.floor(slowest.stats.stamina / (slowest.moveCostMin ?? 40));
        const maxDistToEnemy = Math.max(...allies.map(a => 
            Math.min(...enemies.map(e => getHexDistance(a, e)))
        ));
        
        // Відстань від НАЙБЛИЖЧОГО союзника до найближчого ворога (авангард)
        const minDistToEnemy = Math.min(...allies.map(a => 
            Math.min(...enemies.map(e => getHexDistance(a, e)))
        ));

        const closestEnemyDist = Math.min(...enemies.map(e => getHexDistance(e, slowest)));
        const isSkirmishing = closestEnemyDist < centroidDist * 0.6;
// console.log("minDistToEnemy", closestEnemyDist, minDistToEnemy, hexesPerRound);
        const vanguardActionReady = closestEnemyDist <= hexesPerRound;

        if (!vanguardActionReady && !isSkirmishing) {
            this.state.goals = this.generateMarchGoals(allies, enemies, allyCentroid, enemyCentroid, hexesPerRound, battleMap);
        } else {
            // В бойовому режимі, якщо танк далеко, першим завданням для ДД 
            // має бути "чекати" або "зайняти позицію за танком"
            this.state.goals = this.generateGoals(allies, enemies);
        }
        // // ЛОГІКА: Маршуємо, поки НАЙДАЛЬШИЙ юніт не підійде на дистанцію 1.5 ходу.
        // // Але якщо авангард уже впритул (minDistToEnemy <= 1), виходимо з маршу в будь-якому випадку.
        // if (maxDistToEnemy > hexesPerRound * 1.5 && minDistToEnemy > hexesPerRound && !isSkirmishing) {

        //     this.state.goals = this.generateMarchGoals(allies, enemies, allyCentroid, enemyCentroid, hexesPerRound, battleMap);
        // } else {
        //     this.state.goals = this.generateGoals(allies, enemies);
        // }
        
        console.log(`[AIManager] Turn ${turn}: mode=${!vanguardActionReady && !isSkirmishing ? 'MARCH' : 'COMBAT'} dist=${centroidDist} hexesPerRound=${hexesPerRound} allyCentroid=${JSON.stringify(allyCentroid)} enemyCentroid=${JSON.stringify(enemyCentroid)}`, closestEnemyDist, vanguardActionReady, isSkirmishing, this.state.goals);
    }
    
    getGoalForUnit(
        unit: BattleUnit,
        allies: BattleUnit[],
        enemies: BattleUnit[],
        battleMap: Map<string, Hex>
    ): Goal | null {
        const existingGoalId = this.state.assignments.get(unit.unitId);
        if (existingGoalId) {
            const existingGoal = this.state.goals.find(g => g.id === existingGoalId);
            if (existingGoal && this.isGoalValid(existingGoal, enemies)) {
                console.log(`[AIManager] Unit ${unit.unitId}: Keeping existing goal ${existingGoal.type} - ${existingGoal.targetId || existingGoal.targetPosition }`);
                return existingGoal;
            }
        }
        
        const assignment = this.assignBestGoal(unit, allies, enemies, battleMap);
        
        if (assignment.goal) {
            this.state.assignments.set(unit.unitId, assignment.goal.id);
            assignment.goal.assignedUnits.push(unit.unitId);
            console.log(`[AIManager] Unit ${unit.unitId}: Assigned ${assignment.goal.type} (score: ${assignment.score.toFixed(1)})`, this.state.goals);
        } else {
            console.log(`[AIManager] Unit ${unit.unitId}: No suitable goal, going OPPORTUNISTIC`);
        }
        
        return assignment.goal;
    }
    
    markGoalCompleted(goalId: string): void {
        const goal = this.state.goals.find(g => g.id === goalId);
        if (goal) {
            goal.isActive = false;
            this.state.goalsCompleted++;
        }
    }
    
    getState(): Readonly<ManagerState> {
        return this.state;
    }

    // ═══════════════════════════════════════════════════════════════════════
    // CENTROID
    // ═══════════════════════════════════════════════════════════════════════

    private getCentroid(units: BattleUnit[]): { q: number; r: number } {
        const q = Math.round(units.reduce((sum, u) => sum + u.q, 0) / units.length);
        const r = Math.round(units.reduce((sum, u) => sum + u.r, 0) / units.length);
        return { q, r };
    }

    // ═══════════════════════════════════════════════════════════════════════
    // MARCH GOALS
    // ═══════════════════════════════════════════════════════════════════════


    private generateMarchGoals(
        allies: BattleUnit[],
        enemies: BattleUnit[],
        allyCentroid: { q: number; r: number },
        enemyCentroid: { q: number; r: number },
        hexesPerRound: number,
        battleMap: Map<string, Hex>
    ): Goal[] {
        const goals: Goal[] = [];

        // 1. ЗНАХОДИМО ТАНКА (Vanguard)
        const vanguard = allies.reduce((max, a) => {
            const currentSurvival = (a.stats.hp + (a.stats.armor ?? 0));
            const maxSurvival = (max.stats.hp + (max.stats.armor ?? 0));
            return currentSurvival > maxSurvival ? a : max;
        });

        // 2. ВЕКТОРИ ТА ДИСТАНЦІЇ ВІД ТАНКА
        const dq = enemyCentroid.q - vanguard.q;
        const dr = enemyCentroid.r - vanguard.r;
        
        // [ЗМІНА] Рахуємо Евклідову дистанцію тільки для нормалізації вектора (щоб довжина була точно 1)
        const euclideanDist = Math.sqrt(dq * dq + dr * dr + dq * dr); 
        const normDist = euclideanDist || 1;

        // Нормалізований вектор напрямку руху
        const dirQ = dq / normDist;
        const dirR = dr / normDist;

        // [ЗАЛИШЕНО ЯК БУЛО] Гексагональна дистанція для обмеження дальності ходу
        const distVanguardToEnemy = Math.max(1, getHexDistance(vanguard, enemyCentroid));

        // 3. МАЙБУТНЯ ПОЗИЦІЯ ТАНКА
        // Танк іде на свій максимум, але зупиняється за 1 гекс до ворога
        const moveDist = Math.min(distVanguardToEnemy - 1, hexesPerRound); 
        
        // 1. Дробові координати ІДЕАЛЬНОЇ позиції танка (де він має стояти)
        const floatVanguardFutureQ = vanguard.q + dirQ * moveDist;
        const floatVanguardFutureR = vanguard.r + dirR * moveDist;

        // 2. Створюємо ВІРТУАЛЬНУ ЦІЛЬ на 1 гекс ДАЛІ по вектору
        // Це компенсує те, що система оцінки зупинить юніта за 1 гекс до цілі
        const floatVanguardGoalQ = floatVanguardFutureQ + dirQ;
        const floatVanguardGoalR = floatVanguardFutureR + dirR;

        // Округлюємо віртуальну ціль для передачі в Goal
        const vanguardGoalPos = hexRound(floatVanguardGoalQ, floatVanguardGoalR);

        // 4. РОЗПОДІЛ ЦІЛЕЙ ДЛЯ ВСІХ
        for (const unit of allies) {
            // Цілі для самого Танка
            if (unit.unitId === vanguard.unitId) {
                const goal: Goal = {
                    id: `march_${unit.unitId}_t${this.currentTurn}`,
                    type: GoalType.REPOSITION,
                    priority: 5,
                    targetPosition: coordsToId(vanguardGoalPos),
                    targetId: undefined,
                    assignedUnits: [unit.unitId],
                    isActive: true,
                    maxUnits: 1,
                    reason: `Vanguard march to ${coordsToId(vanguardGoalPos)}`,
                };
                goals.push(goal);
                this.state.assignments.set(unit.unitId, goal.id);
                continue;
            }

            // [ЗМІНА] Замість проекції використовуємо чисте відносне зміщення в Axial координатах
            // Це гарантує, що якщо юніти стояли в одному стовпчику (однакове q), 
            // вони і прийдуть в один стовпчик.
            const relQ = unit.q - vanguard.q;
            const relR = unit.r - vanguard.r;

            // Ранги: швидкі тримаються позаду танка
            const range = unit.skills[0]?.range || 1;
            // Для маршу краще використовувати цілі числа або -0.5, щоб не "розхитувати" округлення
            const rankOffset = range > 2 ? -0.5 : 0; 

            // Нова позиція = Майбутня база танка + початкове зміщення юніта + зміщення рангу
            const targetFloatQ = floatVanguardFutureQ + relQ + (rankOffset * dirQ);
            const targetFloatR = floatVanguardFutureR + relR + (rankOffset * dirR);
            
            const goalFloatQ = targetFloatQ;
            const goalFloatR = targetFloatR;

            const unitVirtualGoalPos = hexRound(goalFloatQ, goalFloatR);

            const goal: Goal = {
                id: `march_${unit.unitId}_t${this.currentTurn}`,
                type: GoalType.REPOSITION,
                priority: 5,
                targetPosition: coordsToId(unitVirtualGoalPos),
                targetId: undefined,
                assignedUnits: [unit.unitId],
                isActive: true,
                maxUnits: 1,
                reason: `March flank to ${coordsToId(unitVirtualGoalPos)}, range=${range}`,
            };

            goals.push(goal);
            this.state.assignments.set(unit.unitId, goal.id);
        }

        this.state.goalsCreated += goals.length;
        return goals;
    }

    /**
     * Зміщує ціль на range гексів назад від ворога
     */
    private offsetFromTarget(
        target: { q: number; r: number },
        enemyCentroid: { q: number; r: number },
        range: number
    ): { q: number; r: number } {
        const dq = target.q - enemyCentroid.q;
        const dr = target.r - enemyCentroid.r;
        const dist = Math.max(1, getHexDistance(target, enemyCentroid));
        return {
            q: Math.round(target.q + (dq / dist) * range),
            r: Math.round(target.r + (dr / dist) * range),
        };
    }

    // ═══════════════════════════════════════════════════════════════════════
    // GOAL GENERATION
    // ═══════════════════════════════════════════════════════════════════════
    
    private generateGoals(
        allies: BattleUnit[],
        enemies: BattleUnit[],
    ): Goal[] {
        const goals: Goal[] = [];

        const avgAllyPower = allies.reduce((sum, a) => sum + EvaluatorUtils.calculateStackPower(a), 0) / allies.length;
        
        for (const enemy of enemies) {
            const targetPower = EvaluatorUtils.calculateStackPower(enemy);
            const offensivePower = EvaluatorUtils.calculateOffensivePower(enemy);
            const priority = Math.min(10, Math.max(1, 
                Math.log(1 + offensivePower) / 2
            ));

            const powerRatio = targetPower / avgAllyPower;
            const maxUnits = powerRatio > 1.2 ? 2 : 1;
            
            const goal: Goal = {
                id: `eliminate_${enemy.unitId}_t${this.currentTurn}`,
                type: GoalType.ELIMINATE,
                priority,
                targetId: enemy.unitId,
                assignedUnits: [],
                isActive: true,
                maxUnits,
                reason: `Power: ${targetPower.toFixed(0)}, offensive: ${offensivePower.toFixed(0)}, ratio: ${powerRatio.toFixed(2)}, maxUnits: ${maxUnits}`,
            };
            
            goals.push(goal);
        }
        
        goals.sort((a, b) => b.priority - a.priority);
        this.state.goalsCreated += goals.length;
        
        return goals;
    }

    // ═══════════════════════════════════════════════════════════════════════
    // GOAL ASSIGNMENT
    // ═══════════════════════════════════════════════════════════════════════

    assignGoalsToAll(allies: BattleUnit[], enemies: BattleUnit[]): void {
        const isMarchMode = this.state.goals.some(g => g.type === GoalType.REPOSITION);
        if (isMarchMode) return;

        const possibilities: { u: string; g: Goal; score: number }[] = [];

        for (const unit of allies) {
            for (const goal of this.state.goals) {
                const target = enemies.find(e => e.unitId === goal.targetId);
                if (!target || target.isDead) continue;
                
                // 1. Отримуємо тактичну вигідність (Зручність + Ефективність)
                const tacticalWeight = this.calculateWeight(unit, target);
                
                // 2. Множимо на стратегічну важливість цілі (Priority)
                const finalScore = tacticalWeight * (goal.priority || 1.0);
                
                possibilities.push({ u: unit.unitId, g: goal, score: finalScore });

                // Дебаг-лог одним рядком для швидкого аналізу
                console.log(`[Decision] ${unit.unitName} -> ${target.unitName}: Score=${finalScore.toFixed(2)} (Tactics=${tacticalWeight.toFixed(2)} * Priority=${goal.priority.toFixed(1)})`);
            }
        }

        possibilities.sort((a, b) => b.score - a.score);

        const busyUnits = new Set<string>();
        const goalSaturation = new Map<string, number>();

        for (const p of possibilities) {
            if (busyUnits.has(p.u)) continue;
            
            const assignedCount = goalSaturation.get(p.g.id) ?? 0;
            const limit = p.g.maxUnits ?? Infinity;

            if (assignedCount < limit) {
                this.state.assignments.set(p.u, p.g.id);
                p.g.assignedUnits.push(p.u);
                busyUnits.add(p.u);
                goalSaturation.set(p.g.id, assignedCount + 1);
            }
        }
    }

    private calculateWeight(unit: BattleUnit, target: BattleUnit): number {
        const dist = getHexDistance({ q: unit.q, r: unit.r }, { q: target.q, r: target.r });
        const range = unit.skills[0]?.range || 1;
        const speed = unit.stats.speed;
        
        // 1. ЕФЕКТ ЗУСИЛЛЯ (Effort)
        const stepsNeeded = Math.max(0, dist - range);
        
        // Штрафуємо сильніше, якщо юніт НЕ може дотягнутися в цей хід
        // Якщо можемо вдарити: множник ~1.0 - 0.8
        // Якщо треба йти хід без атаки: множник падає до 0.3 - 0.1
        const canReachThisTurn = stepsNeeded <= speed;
        const effortDecay = canReachThisTurn 
            ? 1 / (1 + stepsNeeded * 0.1) // Легкий штраф за рух
            : 0.3 / (1 + (stepsNeeded - speed)); // Жорсткий штраф за "простій"

        // 2. БОЙОВА ЕФЕКТИВНІСТЬ
        const unitAP = (unit.stats.armorPenetration || 0) + (unit.skills[0]?.armorPenBonus || 0);
        const targetAP = target.stats.armorPenetration || 0;

        const armorFactor = CombatCalculator.getArmorFactor(unitAP, target.stats.armor);
        const reverseArmorFactor = CombatCalculator.getArmorFactor(targetAP, unit.stats.armor);

        const offensiveEdge = EvaluatorUtils.calculateAvgDmg(unit) * armorFactor;
        const defensiveRisk = EvaluatorUtils.calculateAvgDmg(target) * reverseArmorFactor;

        // 3. ЛОГІКА РИЗИКУ (Залежно від класу)
        const isTank = unit.stats.armor >= 50;
        const isRanged = range > 2;
        
        let riskFactor = 1.0;
        if (isTank) {
            // Танки (Бек, Гридь) хочуть на себе приймати удар небезпечних ворогів
            riskFactor = 1 + (defensiveRisk / 50); 
        } else if (isRanged) {
            // Стрільці бояться тих, хто може боляче вдарити у відповідь
            riskFactor = 1 / (1 + (defensiveRisk / 20));
        } else {
            // Мілі-ДПС (Батир, Варяг) шукають цілі, які вони вб'ють ШВИДШЕ, ніж отримають здачу
            // Вони не бояться ризику так сильно як стрільці, але й не шукають його як танки
            riskFactor = offensiveEdge > defensiveRisk ? 1.2 : 0.8;
        }

        // 4. БОНУС ЗА ВБИВСТВО (Kill-shot priority)
        // Якщо ми можемо вбити ціль прямо зараз, вага злітає вгору
        // const killBonus = (offensiveEdge >= target.hp) ? 2.0 : 1.0;

        // const score = offensiveEdge * riskFactor * effortDecay * killBonus;
        const score = offensiveEdge * riskFactor * effortDecay;

        console.log(`[Weight] ${unit.unitName} -> ${target.unitName}:
            dist=${dist} range=${range} steps=${stepsNeeded} speed=${unit.stats.speed}
            effortDecay=${effortDecay.toFixed(3)}
            offensiveEdge=${offensiveEdge.toFixed(1)} (avgDmg=${EvaluatorUtils.calculateAvgDmg(unit).toFixed(1)} * armor=${armorFactor.toFixed(2)})
            defensiveRisk=${defensiveRisk.toFixed(1)} (avgDmg=${EvaluatorUtils.calculateAvgDmg(target).toFixed(1)} * armor=${reverseArmorFactor.toFixed(2)})
            SCORE=${score.toFixed(4)}`);

        return score;
    }
    
    // ═══════════════════════════════════════════════════════════════════════
    // ASSIGNMENT LOGIC (fallback для getGoalForUnit)
    // ═══════════════════════════════════════════════════════════════════════
    
    private assignBestGoal(
        unit: BattleUnit,
        allies: BattleUnit[],
        enemies: BattleUnit[],
        battleMap: Map<string, Hex>
    ): GoalAssignment {
        const context = {
            allies,
            enemies,
            battleMap,
            getDistance: getHexDistance,
            getTarget: (targetId: string) => enemies.find(e => e.unitId === targetId),
        };
        
        const scored = this.state.goals
            .filter(goal => goal.isActive)
            .filter(goal => this.canUnitPursueGoal(unit, goal, context))
            .map(goal => ({
                goal,
                score: this.evaluateGoalSuitability(unit, goal, context),
            }))
            .sort((a, b) => b.score - a.score);
        
        const bestChoice = scored[0];

        if (!bestChoice) {
            return { unitId: unit.unitId, goal: null, score: 0 };
        }
        
        return {
            unitId: unit.unitId,
            goal: bestChoice.goal,
            score: bestChoice.score,
            alternatives: scored.slice(1, 3).map(s => s.goal),
        };
    }
    
    private canUnitPursueGoal(unit: BattleUnit, goal: Goal, context: any): boolean {
        if (goal.maxUnits && goal.assignedUnits.length >= goal.maxUnits) return false;
        if (goal.targetId) {
            const target = context.getTarget(goal.targetId);
            if (!target) return false;
        }
        return true;
    }
    
    private evaluateGoalSuitability(unit: BattleUnit, goal: Goal, context: any): number {
        const basePriority = goal.priority * 10;
        
        if (goal.targetId) {
            const target = context.getTarget(goal.targetId);
            if (!target) return 0;
            
            const distance = context.getDistance(
                { q: unit.q, r: unit.r },
                { q: target.q, r: target.r }
            );
            
            return basePriority * (100 / (distance + 1)) / 100;
        }
        
        return basePriority;
    }
    
    // ═══════════════════════════════════════════════════════════════════════
    // VALIDATION
    // ═══════════════════════════════════════════════════════════════════════
    
    private isGoalValid(goal: Goal, enemies: BattleUnit[]): boolean {
        if (!goal.isActive) return false;
        if (goal.targetId) {
            const targetExists = enemies.some(e => e.unitId === goal.targetId);
            if (!targetExists) {
                goal.isActive = false;
                goal.reason = 'Target eliminated';
                return false;
            }
        }
        return true;
    }
    
    // ═══════════════════════════════════════════════════════════════════════
    // CUSTOM EVALUATORS
    // ═══════════════════════════════════════════════════════════════════════
    
    private customEvaluators: Map<GoalType, GoalSuitabilityEvaluator> = new Map();
    
    setEvaluator(goalType: GoalType, evaluator: GoalSuitabilityEvaluator): void {
        this.customEvaluators.set(goalType, evaluator);
    }

    public getCurrentTurn() {
        return this.currentTurn;
    }

    public getCurrentUnit() {
        return this.currentUnit;
    }
}